import type { CustomerData, OrderCalculation } from '../../../types/order-calculation';
import type {
    GiftCardResult,
    MemberCardResult,
    TicketOfficeView,
} from '../../../types/ticket-office';
import { Api } from '../api';
import { updateCalc } from './update-calc';

const updateCustomer = (view: TicketOfficeView, member: CustomerData): void => {
    const { state } = view;

    if (!state.customer.name) {
        state.customer.name = member.name;
    }

    if (!state.customer.email) {
        state.customer.email = member.email;
    }

    if (!state.customer.phone) {
        state.customer.phone = member.phone;
    }
};

const addGiftCard = (view: TicketOfficeView, giftCardData: GiftCardResult): void => {
    const { state, update } = view

    if (state.appliedCardCodes.includes(giftCardData.number)) {
        return
    }

    state.appliedCardCodes.push(giftCardData.number)

    if (giftCardData.member) {
        updateCustomer(view, giftCardData.member)
    }

    update()
}

const addMemberCard = (view: TicketOfficeView, memberCard: MemberCardResult): void => {
    const { state, update } = view
    const { calc } = state

    if (calc.memberCard) {
        state.appliedCardCodes = state.appliedCardCodes.filter((code) => code !== calc.memberCard.number)
    }

    state.appliedCardCodes.push(memberCard.number)
    updateCustomer(view, memberCard.member)
    update()
}

const addSerialTicket = (view: TicketOfficeView, serialTicketId: number): void => {
    const { state, update } = view

    if (!state.serialTicketIds.includes(serialTicketId)) {
        state.serialTicketIds.push(serialTicketId)
        update()
    }
}

export const addDiscount = (view: TicketOfficeView, suggestion: AutocompleteSuggestion): void => {
    const data = suggestion.data as {
        memberCard?: MemberCardResult
        giftCard?: GiftCardResult
        serialTicketId?: number
        discountCode?: {
            number: string
            type: string
            amount: number
        }
    }

    if (data.memberCard) {
        addMemberCard(view, data.memberCard)
    }

    if (data.giftCard) {
        addGiftCard(view, data.giftCard)
    }

    if (data.serialTicketId) {
        addSerialTicket(view, data.serialTicketId)
    }

    if (data.discountCode) {
        addDiscountCode(view, data.discountCode.number)
    }

    void sendDiscountsToServer(view)
}

const addDiscountCode = (view: TicketOfficeView, code: string): void => {
    const { state, update } = view

    if (!state.discountCodes.includes(code)) {
        state.discountCodes.push(code)
        update()
    }
}

export const removeSerialTicketDiscount = (view: TicketOfficeView, serialTicketId: number): void => {
    const { state, update } = view

    state.serialTicketIds = state.serialTicketIds.filter((id) => id !== serialTicketId)

    void sendDiscountsToServer(view)
    update()
}

export const removeGiftCard = (view: TicketOfficeView, number: string): void => {
    const { state, update } = view

    state.appliedCardCodes = state.appliedCardCodes.filter((code) => code !== number)
    update()

    void sendDiscountsToServer(view)
}

export const removeMemberCard = (view: TicketOfficeView): void => {
    const { state, update } = view
    const { calc } = state

    if (calc.memberCard) {
        state.appliedCardCodes = state.appliedCardCodes.filter((code) => code !== calc.memberCard.number)
        update()

        void sendDiscountsToServer(view)
    }
}

export const removeDiscountCode = (view: TicketOfficeView, number: string): void => {
    const { state, update } = view

    state.discountCodes = state.discountCodes.filter((code) => code !== number)
    update()

    void sendDiscountsToServer(view)
}

const sendDiscountsToServer = async (view: TicketOfficeView): Promise<void> => {
    const { state } = view
    const { csrfToken, locale, appliedCardCodes, discountCodes, serialTicketIds, sessionId, urls } = state

    const response = await Api.postJson(urls.addSessionDiscounts, csrfToken, {
        cards: appliedCardCodes,
        discountCodes,
        serialTicketIds,
        sessionId,
        locale,
    })

    const data = await response.json() as { calc: OrderCalculation }
    updateCalc(view, data.calc)
}
